import { useDisableModalBackScroll } from '@fukuri-admins/ui/common/Modal/useDisableModalBackScroll';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { useGetClientAdminsClient } from '~/hooks';
import { getEnvFeatureFlag } from '~/utils/getEnvFeatureFlag';
import { useShippingConfirmationDialog } from './hooks';
import { convertToPresentationProps, shouldShowDialog } from './logics';
import { ShippingConfirmationDialogPresentation } from './presentations';
import { ShippingConfirmationDialogEditPresentation } from './presentations/edit';
import { ShippingConfirmationDialogEditLoadingPresentation } from './presentations/editLoading';

type ShippingConfirmationDialogInnerProps = {
  threeLetterCode: string;
};

/**
 * threeLetterCode が確定している場合にクライアント情報を取得し、表示条件を満たすときだけダイアログを描画する。
 *
 * `useGetClientAdminsClient` は suspense 前提のため、threeLetterCode を持つ場合のみ描画される
 * コンポーネントとして分離し、hook を条件付きで呼び出さずに済ませている（RootRedirect と同じ構成）。
 */
const ShippingConfirmationDialogInner: React.FC<ShippingConfirmationDialogInnerProps> = ({
  threeLetterCode,
}) => {
  const { data } = useGetClientAdminsClient({ threeLetterCode });

  const {
    state: { isMutating, viewMode },
    actions: { handleConfirm, handleEdit, handleEditCancel, handleEditSaved },
  } = useShippingConfirmationDialog({ threeLetterCode });

  const shouldShow = shouldShowDialog(data);

  // ダイアログ表示中は背景スクロールを抑止
  useDisableModalBackScroll({ isOpen: shouldShow });

  if (!shouldShow) {
    return null;
  }

  // 編集ビューは都道府県一覧を suspense 前提で取得するため、ここで境界を持つ。
  // 外側の Suspense（fallback null）に到達するとダイアログごと消えてしまう
  if (viewMode === 'edit') {
    return (
      <Suspense fallback={<ShippingConfirmationDialogEditLoadingPresentation />}>
        <ShippingConfirmationDialogEditPresentation
          threeLetterCode={threeLetterCode}
          onCancel={handleEditCancel}
          onSaved={handleEditSaved}
        />
      </Suspense>
    );
  }

  // convertToPresentationProps は shipping_address の存在を前提とするため、表示条件を満たす場合のみ呼ぶ
  const { title, employeeCount, shippingAddress, isConfirming } = convertToPresentationProps({
    data,
    isMutating,
  });

  return (
    <ShippingConfirmationDialogPresentation
      title={title}
      employeeCount={employeeCount}
      shippingAddress={shippingAddress}
      isConfirming={isConfirming}
      onConfirm={handleConfirm}
      onEdit={handleEdit}
    />
  );
};

/**
 * 送付先住所確認ダイアログのガード型 feature
 *
 * `<Outlet />` を描画しつつ、未確認の送付先住所がある場合にダイアログを重ねて表示する。
 * App.tsx の ProtectedRoute 直下に配置することで、ログイン後の全ルートに対して
 * ダイアログを表示可能にする（`/contact` ルートは例外として扱う）。
 *
 * 表示条件:
 * - 利用開始カード送付希望（wants_activation_card = true）
 * - 送付先住所が登録済み（shipping_address が存在）
 * - 送付先住所が未確認（shipping_address.confirmed_at = null）
 *
 * 表示しない条件:
 * - 利用開始カード不要（wants_activation_card = false）
 * - 送付先住所が未登録（shipping_address = null）
 * - 送付先住所が確認済み（shipping_address.confirmed_at ≠ null）
 *
 * `enableStaging` が false の環境（本番・demo）では判定も取得も行わず `<Outlet />` のみ描画する。
 * WelcomeKit（本 feature を含む）はまだ本番公開されていないため。本 feature は確認が完了するまで
 * 他の操作を塞ぐ仕様のため、公開前に表示されると管理画面全体が操作不能になる。
 * ガード Route は全ルートを包むため App.tsx 側で条件分岐するとルート定義が二重になる。そのため
 * 環境判定はこの Container で行う。
 *
 * threeLetterCode が取得できない場合は判定せず `<Outlet />` のみ描画する
 * （空だと suspense hook のキーが安定せずフェッチが固着するため、hook を呼ぶ前に弾く）。
 *
 * ダイアログ側だけを `Suspense` で包み、`<Outlet />` はその外に置く。App.tsx の Route 階層には
 * Suspense 境界が無く、Screen 層の境界より上に位置するため、ここで境界を持たないと初回フェッチで
 * 配下の全ルートが fallback 無しで suspend する。fallback を null にしているのは、ページ本体の
 * 描画をクライアント情報の取得完了まで待たせないため（ダイアログは取得後に重なる）。
 *
 * @see RootRedirect - 同様のガード型 feature の参考実装
 */
export const ShippingConfirmationDialog: React.FC = () => {
  const { enableStaging } = getEnvFeatureFlag();
  const threeLetterCode = getThreeLetterCode();
  if (!enableStaging || !threeLetterCode) {
    return <Outlet />;
  }

  return (
    <>
      <Outlet />
      <Suspense fallback={null}>
        <ShippingConfirmationDialogInner threeLetterCode={threeLetterCode} />
      </Suspense>
    </>
  );
};
